import { MutableRefObject } from 'react';
import { Group, PerspectiveCamera } from 'three';
import { gsap, Power1 } from 'gsap';
import {
    KintekiArrowModel,
    EntekiArrowModel,
    MakiwaraArrowModel,
} from '@app/react/3d/ArrowModelInterface';
import { toRad } from '@app/react/common/toRad';
import { OrbitControls } from '@app/react/uikit/OrbitControls';

const { easeInOut } = Power1;

type AllModel = KintekiArrowModel | EntekiArrowModel | MakiwaraArrowModel | null;

// TODO: fixme duplicate define
interface SceneHander {
    setModel: (model: Group) => void;
    addModel: (model: Group) => void;
    removeModel: () => void;
    control: () => OrbitControls | null;
}

export interface DesktopAnimation {
    reset: () => void;
    home: () => void;
    shaft: () => void;
    feather: () => void;
    yajiri: () => void;
    yashaku: () => void;
    carving: () => void;
}

export interface MobileAnimation {
    shaft: () => void;
    carving: () => void;
    familyCrest: () => void;
    preview1: () => void;
}

type SetupAnimation = (
    modelRef: MutableRefObject<AllModel>,
    modelHandleRef: MutableRefObject<SceneHander | null>,
) => ({ desktop: DesktopAnimation, mobile: MobileAnimation });

export const setupAnimation: SetupAnimation = (modelRef, modelHandleRef) => {
    const desktop = {
        reset: () => {
            const model = modelRef.current;
            const target = modelHandleRef.current?.control()?.target ?? null;
            const object: PerspectiveCamera | null = modelHandleRef.current?.control()?.object;
            const position = object?.position ?? null;
            if (model === null || target === null || position === null) {
                return;
            }

            gsap.to(model.model.position, { x: 0, y: 0, z: 0, easing: easeInOut });
            gsap.to(model.model.rotation, { x: 0, y: 0, z: 0, easing: easeInOut });
            gsap.to(target, { x: 0, y: 0, z: 0 });
            gsap.to(position, { x: 0, y: 0, z: 100, easing: easeInOut });
        },
        home: () => {
            const model = modelRef.current;
            const target = modelHandleRef.current?.control()?.target ?? null;
            const object: PerspectiveCamera | null = modelHandleRef.current?.control()?.object;
            const position = object?.position ?? null;
            if (model === null || target === null || position === null) {
                return;
            }

            gsap.to(position, { x: 0, y: 0, z: 20, easing: easeInOut });
            gsap.to(target, { x: 0, y: 0, z: 0, easing: easeInOut });
            gsap.to(model.model.position, { x: -10, y: -12, z: 0, easing: easeInOut });
            gsap.to(model.model.rotation, { x: 0, y: 0, z: toRad(50), easing: easeInOut });
        },
        shaft: () => {
            const model = modelRef.current;
            const target = modelHandleRef.current?.control()?.target ?? null;
            const object: PerspectiveCamera | null = modelHandleRef.current?.control()?.object;
            const position = object?.position ?? null;
            if (model === null || target === null || position === null) {
                return;
            }

            gsap.to(position, { x: 0, y: 0, z: 25, easing: easeInOut });
            gsap.to(target, { x: 0, y: 0, z: 0, easing: easeInOut });
            gsap.to(model.model.position, { x: -8.5, y: -10, z: 0, easing: easeInOut });
            gsap.to(model.model.rotation, { x: 0, y: 0, z: toRad(50), easing: easeInOut });
        },
        feather: () => {
            const model = modelRef.current;
            const target = modelHandleRef.current?.control()?.target ?? null;
            const object: PerspectiveCamera | null = modelHandleRef.current?.control()?.object;
            const position = object?.position ?? null;
            if (model === null || target === null || position === null) {
                return;
            }

            gsap.to(position, { x: 0, y: 0, z: 18, easing: easeInOut });
            gsap.to(target, { x: 0, y: 0, z: 0, easing: easeInOut });
            gsap.to(model.model.position, { x: -10, y: -12, z: 0, easing: easeInOut });
            gsap.to(model.model.rotation, { x: 0, y: 0, z: toRad(50), easing: easeInOut });
        },
        yajiri: () => {
            const model = modelRef.current;
            const target = modelHandleRef.current?.control()?.target ?? null;
            const object: PerspectiveCamera | null = modelHandleRef.current?.control()?.object;
            const position = object?.position ?? null;
            if (model === null || target === null || position === null) {
                return;
            }

            gsap.to(position, { x: 0, y: 0, z: 25, easing: easeInOut });
            gsap.to(target, { x: 0, y: 0, z: 0, easing: easeInOut });
            gsap.to(model.model.position, { x: 12, y: 14.5, z: 0, easing: easeInOut });
            gsap.to(model.model.rotation, { x: 0, y: 0, z: toRad(50), easing: easeInOut });
        },
        yashaku: () => {
            const model = modelRef.current;
            const target = modelHandleRef.current?.control()?.target ?? null;
            const object: PerspectiveCamera | null = modelHandleRef.current?.control()?.object;
            const position = object?.position ?? null;
            if (model === null || target === null || position === null) {
                return;
            }

            gsap.to(position, { x: 0, y: 0, z: 70, easing: easeInOut });
            gsap.to(target, { x: 0, y: 0, z: 0, easing: easeInOut });
            gsap.to(model.model.position, { x: 0, y: 0, z: 0, easing: easeInOut });
            gsap.to(model.model.rotation, { x: 0, y: 0, z: toRad(50), easing: easeInOut });
        },
        carving: () => {
            const model = modelRef.current;
            const target = modelHandleRef.current?.control()?.target ?? null;
            const object: PerspectiveCamera | null = modelHandleRef.current?.control()?.object;
            const position = object?.position ?? null;
            if (model === null || target === null || position === null) {
                return;
            }

            gsap.to(position, { x: 0, y: 0, z: 18, easing: easeInOut });
            gsap.to(target, { x: 0, y: 0, z: 0, easing: easeInOut });
            gsap.to(model.model.position, { x: -6.5, y: -7.5, z: 0, easing: easeInOut });
            gsap.to(model.model.rotation, { x: 0, y: 0, z: toRad(50), easing: easeInOut });
        },
    };

    const mobile = {
        shaft: () => {
            const model = modelRef.current;
            const target = modelHandleRef.current?.control()?.target ?? null;
            const object: PerspectiveCamera | null = modelHandleRef.current?.control()?.object;
            const position = object?.position ?? null;
            if (model === null || target === null || position === null) {
                return;
            }

            gsap.to(position, { x: 0, y: 0, z: 23, easing: easeInOut });
            gsap.to(target, { x: 0, y: 0, z: 0, easing: easeInOut });
            gsap.to(model.model.position, { x: -0, y: -15.3, z: 0, easing: easeInOut });
            gsap.to(model.model.rotation, { x: 0, y: 0, z: toRad(90), easing: easeInOut });
        },
        carving: () => {
            const model = modelRef.current;
            const target = modelHandleRef.current?.control()?.target ?? null;
            const object: PerspectiveCamera | null = modelHandleRef.current?.control()?.object;
            const position = object?.position ?? null;
            if (model === null || target === null || position === null) {
                return;
            }

            gsap.to(position, { x: 0, y: 0, z: 30, easing: easeInOut });
            gsap.to(target, { x: 0, y: 0, z: 0, easing: easeInOut });
            gsap.to(model.model.position, { x: -5, y: -7, z: 0, easing: easeInOut });
            gsap.to(model.model.rotation, { x: 0, y: 0, z: toRad(60), easing: easeInOut });
        },
        familyCrest: () => {
            const model = modelRef.current;
            const target = modelHandleRef.current?.control()?.target ?? null;
            const object: PerspectiveCamera | null = modelHandleRef.current?.control()?.object;
            const position = object?.position ?? null;
            if (model === null || target === null || position === null) {
                return;
            }

            gsap.to(position, { x: 0, y: 0, z: 45, easing: easeInOut });
            gsap.to(target, { x: 0, y: 0, z: 0, easing: easeInOut });
            gsap.to(model.model.position, { x: -8, y: -9, z: 0, easing: easeInOut });
            gsap.to(model.model.rotation, { x: 0, y: 0, z: toRad(60), easing: easeInOut });
        },
        preview1: () => {
            const model = modelRef.current;
            const target = modelHandleRef.current?.control()?.target ?? null;
            const object: PerspectiveCamera | null = modelHandleRef.current?.control()?.object;
            const position = object?.position ?? null;
            if (model === null || target === null || position === null) {
                return;
            }

            gsap.to(position, { x: 0, y: 0, z: 110, easing: easeInOut });
            gsap.to(target, { x: 0, y: 0, z: 0, easing: easeInOut });
            gsap.to(model.model.position, { x: 0, y: -1.5, z: 0, easing: easeInOut });
            gsap.to(model.model.rotation, { x: 0, y: 0, z: toRad(70), easing: easeInOut });
        },
    };

    return {
        desktop,
        mobile,
    };
};
